import { eq, and, sql, isNull, isNotNull, gt } from 'drizzle-orm';
import { db, runInTransaction } from '@/db/client';
import { installments, transactions, transactionTags, creditCards, categories, invoices, accounts } from '@/db/schema';
import { generateId } from '@/lib/id';
import { resolveTransactionTime } from '@/lib/transactionTime';
import { getOrCreateInvoice, getInvoiceMonthForDate, recalculateInvoiceTotal } from './invoiceRepository';
import { recalculateAccountBalance } from './accountRepository';
import { format, addMonths, parse, differenceInCalendarMonths, getDaysInMonth, setDate, subMonths } from 'date-fns';
import { getInstallmentAmountForNumber, normalizeCurrentInstallment } from '@/lib/installments';
import { parcelMonthKey, type InstallmentParcelRow } from '@/lib/installmentPlans';
import { getMonthStartDay } from '@/lib/monthStart';
import { excludeDeletedCardTransactions, excludeHiddenAccountTransactions, originCardIdExpr } from './transactionScopes';

export type Installment = typeof installments.$inferSelect;

export type InstallmentWithDetails = Installment & {
    creditCardName?: string | null;
    creditCardColor?: string | null;
    categoryName?: string | null;
    categoryIcon?: string | null;
    categoryColor?: string | null;
    paidCount?: number;
    linkedTransactionId?: string | null;
};

export async function createInstallment(data: {
    description: string;
    totalAmount: number;
    installmentCount: number;
    startInstallmentNumber?: number;
    creditCardId: string;
    invoiceMonth?: string;
    notes?: string;
    categoryId?: string;
    subcategoryId?: string;
    date: string;
    /**
     * Hora da COMPRA (HH:MM), como em qualquer outro lançamento. Vale para a série
     * inteira, igual a `notes`/`tagIds`: descreve a compra que gerou as parcelas, e é
     * o desempate intra-dia do extrato. Sem ela aqui, o parcelado no cartão era o
     * único caminho que perdia a hora — o parcelado em conta já gravava.
     */
    time?: string;
    // Metadados do formulário que pertencem a TODAS as parcelas da série — não só à
    // primeira. Sem eles aqui, o caller teria que pós-processar N transações (e era
    // exatamente o que ninguém fazia: tags/ignorar se perdiam no parcelado).
    isIgnored?: boolean;
    tagIds?: string[];
    /** Série criada a partir de uma fatura IMPORTADA — ver `createTransaction`. */
    isImported?: boolean;
}): Promise<Installment & { firstTransactionId: string }> {
    const card = await db.select().from(creditCards).where(eq(creditCards.id, data.creditCardId));
    if (!card[0]) throw new Error('Credit card not found');

    const startInstallmentNumber = normalizeCurrentInstallment(
        data.startInstallmentNumber ?? 1,
        data.installmentCount,
    );
    // Resolvida UMA vez, a partir da data da COMPRA: a série inteira herda a mesma hora
    // (como notes/tags), e as parcelas futuras não podem cada uma calcular a sua.
    const purchaseTime = resolveTransactionTime(data.date, data.time, { stampNow: !data.isImported });
    const installmentAmount = getInstallmentAmountForNumber(data.totalAmount, data.installmentCount, 1);
    const id = generateId();
    const now = new Date().toISOString();
    const trackedDescription =
        startInstallmentNumber > 1
            ? `${data.description} (a partir de ${startInstallmentNumber}/${data.installmentCount})`
            : data.description;

    // Determine the first invoice month based on purchase date and closing day
    const closingDayToNext = card[0].closingDayToNextInvoice === 1;
    const firstInvoiceMonth = data.invoiceMonth ?? getInvoiceMonthForDate(data.date, card[0].closingDay, closingDayToNext);
    const basePurchaseDate = parse(data.date, 'yyyy-MM-dd', new Date());

    // Base "velha": a data da compra está a mais de um mês da primeira fatura. Acontece na
    // importação de fatura com parcela antiga (a fatura imprime a data da COMPRA ORIGINAL:
    // uma 14/24 numa fatura de jan/2026 veio de dez/2024). Marchar as datas futuras a partir
    // dessa base espalhava lançamentos por anos passados no extrato — as parcelas projetadas
    // passam a ser datadas junto da SUA fatura (a competência explícita já era a certa).
    // A parcela importada (a primeira da série) mantém a data impressa, como no manual.
    // No fluxo manual a distância é 0–1 mês e nada muda.
    const firstInvoiceDate = parse(firstInvoiceMonth + '-01', 'yyyy-MM-dd', new Date());
    const staleBaseDate = differenceInCalendarMonths(firstInvoiceDate, basePurchaseDate) > 1;

    // Atômico (M1, auditoria jul/2026): o registro do parcelamento + as N parcelas nascem
    // JUNTOS. Sem transação, um kill no meio do loop deixava um parcelamento pela metade —
    // total certo mas parcelas de menos, faltando na projeção e sem reparo pela UI.
    return runInTransaction(async () => {
        await db.insert(installments).values({
            id,
            description: trackedDescription,
            totalAmount: data.totalAmount,
            installmentCount: data.installmentCount,
            installmentAmount,
            creditCardId: data.creditCardId,
            categoryId: data.categoryId ?? null,
            subcategoryId: data.subcategoryId ?? null,
            startDate: data.date,
            isActive: 1,
            createdAt: now,
            updatedAt: now,
        });

        // Create only the current installment and future ones from the selected starting point.
        let firstTransactionId = '';
        for (let installmentNumber = startInstallmentNumber; installmentNumber <= data.installmentCount; installmentNumber++) {
            const offset = installmentNumber - startInstallmentNumber;
            const targetMonth = format(addMonths(firstInvoiceDate, offset), 'yyyy-MM');
            const plannedDate = staleBaseDate && offset > 0
                ? dateWithinInvoiceMonth(targetMonth, basePurchaseDate.getDate(), card[0].closingDay, closingDayToNext)
                : format(addMonths(basePurchaseDate, offset), 'yyyy-MM-dd');

            // Get or create the invoice for this month
            const invoice = await getOrCreateInvoice(data.creditCardId, targetMonth);

            const amount = getInstallmentAmountForNumber(
                data.totalAmount,
                data.installmentCount,
                installmentNumber,
            );

            const txId = generateId();
            if (installmentNumber === startInstallmentNumber) firstTransactionId = txId;
            await db.insert(transactions).values({
                id: txId,
                type: 'expense',
                amount,
                description: `${data.description} (${installmentNumber}/${data.installmentCount})`,
                date: plannedDate,
                time: purchaseTime,
                notes: data.notes || null,
                creditCardId: data.creditCardId,
                invoiceId: invoice.id,
                categoryId: data.categoryId ?? null,
                subcategoryId: data.subcategoryId ?? null,
                installmentId: id,
                installmentNumber,
                installmentTotal: data.installmentCount,
                isConfirmed: 1,
                isIgnored: data.isIgnored ? 1 : 0,
                createdAt: now,
                updatedAt: now,
            });

            if (data.tagIds && data.tagIds.length > 0) {
                await db.insert(transactionTags).values(
                    data.tagIds.map((tagId) => ({ transactionId: txId, tagId })),
                );
            }

            // Recalculate invoice total
            await recalculateInvoiceTotal(invoice.id);
        }

        const result = await db.select().from(installments).where(eq(installments.id, id));
        return { ...result[0], firstTransactionId };
    });
}

/**
 * Data com o dia da compra original cuja fatura natural (getInvoiceMonthForDate)
 * é `invoiceMonth`: dia até o fechamento fica no próprio mês; depois do
 * fechamento, no mês anterior. Se o clamp de fim de mês quebrar a conta (ex.:
 * dia 31 com fevereiro no meio), cai no próprio dia de fechamento da fatura —
 * que pertence a ela por definição.
 */
function dateWithinInvoiceMonth(
    invoiceMonth: string,
    purchaseDay: number,
    closingDay: number,
    closingDayToNextInvoice = false,
): string {
    const monthDate = parse(invoiceMonth + '-01', 'yyyy-MM-dd', new Date());
    // Com a compra do dia do fechamento indo pra seguinte (ADR 0130), o dia do fechamento já
    // é do mês anterior da janela; o último dia que ainda pertence à fatura é a véspera.
    const staysInMonth = closingDayToNextInvoice ? purchaseDay < closingDay : purchaseDay <= closingDay;
    const anchor = staysInMonth ? monthDate : subMonths(monthDate, 1);
    const candidate = format(setDate(anchor, Math.min(purchaseDay, getDaysInMonth(anchor))), 'yyyy-MM-dd');
    if (getInvoiceMonthForDate(candidate, closingDay, closingDayToNextInvoice) === invoiceMonth) return candidate;
    const lastDayInMonth = closingDayToNextInvoice ? closingDay - 1 : closingDay;
    if (lastDayInMonth < 1) {
        // Fecha dia 1 com a compra do dia indo pra seguinte: a janela é o mês anterior inteiro.
        const prev = subMonths(monthDate, 1);
        return format(setDate(prev, getDaysInMonth(prev)), 'yyyy-MM-dd');
    }
    return format(setDate(monthDate, Math.min(lastDayInMonth, getDaysInMonth(monthDate))), 'yyyy-MM-dd');
}

export async function getInstallmentsByCard(cardId: string): Promise<InstallmentWithDetails[]> {
    const rows = await db
        .select({
            id: installments.id,
            description: installments.description,
            totalAmount: installments.totalAmount,
            installmentCount: installments.installmentCount,
            installmentAmount: installments.installmentAmount,
            creditCardId: installments.creditCardId,
            categoryId: installments.categoryId,
            subcategoryId: installments.subcategoryId,
            startDate: installments.startDate,
            isActive: installments.isActive,
            createdAt: installments.createdAt,
            updatedAt: installments.updatedAt,
            creditCardName: creditCards.name,
            creditCardColor: creditCards.color,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
        })
        .from(installments)
        .innerJoin(creditCards, eq(installments.creditCardId, creditCards.id))
        .leftJoin(categories, eq(installments.categoryId, categories.id))
        .where(
            and(
                eq(installments.creditCardId, cardId),
                eq(installments.isActive, 1),
            ),
        );

    // Count paid installments for each
    const result: InstallmentWithDetails[] = [];
    for (const row of rows) {
        const [paidResult, linkedTransaction] = await Promise.all([
            db
                .select({
                    count: sql<number>`COUNT(*)`.as('count'),
                })
                .from(transactions)
                .where(
                    and(
                        eq(transactions.installmentId, row.id),
                        isNull(transactions.deletedAt),
                        sql`${transactions.invoiceId} IN (
                            SELECT id FROM invoices WHERE status IN ('paid')
                        )`,
                    ),
                ),
            db
                .select({
                    id: transactions.id,
                })
                .from(transactions)
                .leftJoin(invoices, eq(transactions.invoiceId, invoices.id))
                .where(
                    and(
                        eq(transactions.installmentId, row.id),
                        isNull(transactions.deletedAt),
                    ),
                )
                // Prefer the next unpaid installment so "future" delete/edit actions
                // start from the remaining active portion of the series.
                .orderBy(
                    sql`CASE WHEN ${invoices.status} = 'paid' THEN 1 ELSE 0 END`,
                    sql`COALESCE(${transactions.installmentNumber}, 999999)`,
                    transactions.date,
                    transactions.createdAt,
                )
                .limit(1),
        ]);

        result.push({
            ...row,
            paidCount: paidResult[0]?.count ?? 0,
            linkedTransactionId: linkedTransaction[0]?.id ?? null,
        });
    }

    return result;
}

export async function getActiveInstallments(): Promise<InstallmentWithDetails[]> {
    const rows = await db
        .select({
            id: installments.id,
            description: installments.description,
            totalAmount: installments.totalAmount,
            installmentCount: installments.installmentCount,
            installmentAmount: installments.installmentAmount,
            creditCardId: installments.creditCardId,
            categoryId: installments.categoryId,
            subcategoryId: installments.subcategoryId,
            startDate: installments.startDate,
            isActive: installments.isActive,
            createdAt: installments.createdAt,
            updatedAt: installments.updatedAt,
            creditCardName: creditCards.name,
            creditCardColor: creditCards.color,
            categoryName: categories.name,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
        })
        .from(installments)
        .innerJoin(creditCards, eq(installments.creditCardId, creditCards.id))
        .leftJoin(categories, eq(installments.categoryId, categories.id))
        .where(eq(installments.isActive, 1));

    const result: InstallmentWithDetails[] = [];
    for (const row of rows) {
        const [paidResult, linkedTransaction] = await Promise.all([
            db
                .select({
                    count: sql<number>`COUNT(*)`.as('count'),
                })
                .from(transactions)
                .where(
                    and(
                        eq(transactions.installmentId, row.id),
                        isNull(transactions.deletedAt),
                        sql`${transactions.invoiceId} IN (
                            SELECT id FROM invoices WHERE status IN ('paid')
                        )`,
                    ),
                ),
            db
                .select({
                    id: transactions.id,
                })
                .from(transactions)
                .leftJoin(invoices, eq(transactions.invoiceId, invoices.id))
                .where(
                    and(
                        eq(transactions.installmentId, row.id),
                        isNull(transactions.deletedAt),
                    ),
                )
                .orderBy(
                    sql`CASE WHEN ${invoices.status} = 'paid' THEN 1 ELSE 0 END`,
                    sql`COALESCE(${transactions.installmentNumber}, 999999)`,
                    transactions.date,
                    transactions.createdAt,
                )
                .limit(1),
        ]);

        result.push({
            ...row,
            paidCount: paidResult[0]?.count ?? 0,
            linkedTransactionId: linkedTransaction[0]?.id ?? null,
        });
    }

    return result;
}

/**
 * Todas as parcelas de compra parcelada, de cartão e de conta, para a tela Parcelamentos
 * (ADR 0166). Lê pelas TRANSAÇÕES: parcelado em conta não tem linha em `installments`, e
 * cabeçalho de cartão desativado (série que saiu do cartão) deixaria a série de fora.
 *
 * O mês de cada parcela é o mês em que ela PESA: no cartão, o do vencimento da fatura
 * (é como o banco nomeia a fatura, ADR 0102); na conta, o da própria data. Parcela de
 * cartão sem fatura (não deveria existir) cai no mês da data.
 *
 * Fora: estorno e artefato de fatura (não são compra), cartão excluído (ADR 0068) e conta
 * arquivada ou fora do total, que também não entram em projeção nenhuma (ADR 0061). O
 * "ignorar" NÃO tira a parcela daqui: ele sai das leituras analíticas, mas a parcela
 * continua sendo cobrada na fatura.
 */
export async function getInstallmentParcelRows(
    options?: { monthStartDay?: number },
): Promise<InstallmentParcelRow[]> {
    // Início do mês (ADR 0190): a parcela pesa no PERÍODO que contém o vencimento (ou a data).
    const monthStartDay = options?.monthStartDay ?? getMonthStartDay();
    const originCardId = originCardIdExpr();
    const rows = await db
        .select({
            installmentId: transactions.installmentId,
            installmentNumber: transactions.installmentNumber,
            installmentTotal: transactions.installmentTotal,
            amount: transactions.amount,
            date: transactions.date,
            dueDate: invoices.dueDate,
            description: transactions.description,
            categoryIcon: categories.icon,
            categoryColor: categories.color,
            cardId: creditCards.id,
            cardName: creditCards.name,
            cardColor: creditCards.color,
            cardInstitution: creditCards.institution,
            cardInstitutionIcon: creditCards.institutionIcon,
            accountId: accounts.id,
            accountName: accounts.name,
            accountColor: accounts.color,
            accountInstitution: accounts.institution,
            accountInstitutionIcon: accounts.institutionIcon,
        })
        .from(transactions)
        .leftJoin(invoices, eq(transactions.invoiceId, invoices.id))
        .leftJoin(creditCards, sql`${creditCards.id} = ${originCardId}`)
        .leftJoin(accounts, eq(transactions.accountId, accounts.id))
        .leftJoin(categories, eq(transactions.categoryId, categories.id))
        .where(
            and(
                isNotNull(transactions.installmentId),
                isNull(transactions.deletedAt),
                eq(transactions.type, 'expense'),
                isNull(transactions.adjustmentType),
                gt(transactions.amount, 0),
                excludeDeletedCardTransactions(),
                excludeHiddenAccountTransactions(),
            ),
        );

    const parcels: InstallmentParcelRow[] = [];
    for (const row of rows) {
        const onCard = row.cardId != null;
        const sourceId = onCard ? row.cardId : row.accountId;
        if (!row.installmentId || !sourceId) continue;
        parcels.push({
            installmentId: row.installmentId,
            installmentNumber: row.installmentNumber,
            installmentTotal: row.installmentTotal,
            amount: row.amount,
            month: parcelMonthKey({ date: row.date, dueDate: row.dueDate, onCard }, monthStartDay),
            description: row.description,
            categoryIcon: row.categoryIcon,
            categoryColor: row.categoryColor,
            sourceKind: onCard ? 'card' : 'account',
            sourceId,
            sourceName: (onCard ? row.cardName : row.accountName) ?? '',
            sourceColor: onCard ? row.cardColor : row.accountColor,
            sourceInstitution: onCard ? row.cardInstitution : row.accountInstitution,
            sourceInstitutionIcon: onCard ? row.cardInstitutionIcon : row.accountInstitutionIcon,
        });
    }
    return parcels;
}

export async function getFutureCommitments(
    cardId: string,
    months = 6,
): Promise<{ month: string; amount: number }[]> {
    const currentMonth = format(new Date(), 'yyyy-MM');
    const result: { month: string; amount: number }[] = [];

    for (let i = 0; i < months; i++) {
        const targetMonth = format(
            addMonths(parse(currentMonth + '-01', 'yyyy-MM-dd', new Date()), i + 1),
            'yyyy-MM',
        );

        const sum = await db
            .select({
                total: sql<number>`COALESCE(SUM(${transactions.amount}), 0)`.as('total'),
            })
            .from(transactions)
            .where(
                and(
                    eq(transactions.creditCardId, cardId),
                    sql`${transactions.installmentId} IS NOT NULL`,
                    sql`${transactions.invoiceId} IN (
                        SELECT id FROM invoices WHERE reference_month = ${targetMonth}
                    )`,
                    isNull(transactions.deletedAt),
                ),
            );

        if (sum[0]?.total > 0) {
            result.push({ month: targetMonth, amount: sum[0].total });
        }
    }

    return result;
}

/**
 * Números das parcelas que hoje vivem numa fatura JÁ COM PAGAMENTO.
 *
 * É o que o formulário precisa para não oferecer um escopo que o repositório vai recusar:
 * o remanejo de série não arrasta parcela para dentro nem para fora de fatura paga
 * (ADR 0086, barreira em `updateInstallmentSeries`). A âncora fica de fora da conta lá,
 * então quem chama aqui compara com o próprio número da parcela em edição.
 */
export async function getSettledInvoiceInstallmentNumbers(installmentId: string): Promise<number[]> {
    const rows = await db
        .select({ installmentNumber: transactions.installmentNumber })
        .from(transactions)
        .innerJoin(invoices, eq(transactions.invoiceId, invoices.id))
        .where(and(
            eq(transactions.installmentId, installmentId),
            isNull(transactions.deletedAt),
            sql`${invoices.paidAmount} > 0`,
        ));

    return [...new Set(
        rows.map((row) => row.installmentNumber).filter((number): number is number => number != null),
    )].sort((left, right) => left - right);
}

export async function deleteInstallment(installmentId: string): Promise<void> {
    const relatedTxs = await db
        .select({ id: transactions.id, accountId: transactions.accountId, invoiceId: transactions.invoiceId })
        .from(transactions)
        .where(and(eq(transactions.installmentId, installmentId), isNull(transactions.deletedAt)));

    const now = new Date().toISOString();

    // Soft-delete all transactions linked to this installment
    if (relatedTxs.length > 0) {
        await db
            .update(transactions)
            .set({ deletedAt: now })
            .where(and(eq(transactions.installmentId, installmentId), isNull(transactions.deletedAt)));
    }

    // Deactivate the installment record
    await db
        .update(installments)
        .set({ isActive: 0, updatedAt: now })
        .where(eq(installments.id, installmentId));

    // Recalculate affected invoices and accounts
    const affectedInvoices = new Set<string>();
    const affectedAccounts = new Set<string>();

    for (const tx of relatedTxs) {
        if (tx.invoiceId) affectedInvoices.add(tx.invoiceId);
        if (tx.accountId) affectedAccounts.add(tx.accountId);
    }

    for (const invoiceId of affectedInvoices) {
        await recalculateInvoiceTotal(invoiceId);
    }
    for (const accountId of affectedAccounts) {
        await recalculateAccountBalance(accountId);
    }
}
